import { useStore } from '@nanostores/react'
import { atom, computed, map } from 'nanostores'

import type { AttachResponse, PreviewFile } from '../model/types'

const $fileMap = map<Record<string, PreviewFile>>({})
const ids = new Set<string>()
const $rerender = atom(0)
const rerenderList = () => $rerender.set($rerender.get() + 1)

const enum FilesState {
	Empty,
	InProgress,
	Ready
}

function add(file: PreviewFile) {
	ids.add(file.id)
	$fileMap.setKey(file.id, file)
	rerenderList()
}

function clear() {
	ids.clear()
	$fileMap.set({})
	rerenderList()
}

function remove(id: string) {
	ids.delete(id)
	$fileMap.setKey(id, undefined)
	rerenderList()
}

function saveAttach({ attach, id }: Pick<PreviewFile, 'attach' | 'id'>) {
	updateFile(id, { attach, loaded: 100 })
}

function updateFile(id: string, update: Partial<PreviewFile>) {
	const file = $fileMap.get()[id]

	if (file) {
		$fileMap.setKey(id, { ...file, ...update })
	}
}

const setError = ({ error, id }: Pick<PreviewFile, 'error' | 'id'>) =>
	updateFile(id, { error, loaded: 0 })

function updateLoaded({ id, loaded }: Pick<PreviewFile, 'id' | 'loaded'>) {
	updateFile(id, { loaded })
}

const $fileState = computed($fileMap, files => {
	if (ids.size === 0) {
		return FilesState.Empty
	}

	for (const id of ids) {
		const file = files[id]
		if (file && file.loaded < 100) {
			return FilesState.InProgress
		}
	}

	return FilesState.Ready
})

function getAttachments() {
	const attaches: AttachResponse[] = []
	const map = $fileMap.get()

	for (const id of ids) {
		const file = map[id]
		if (file && file.attach) {
			attaches.push(file.attach)
		}
	}

	return attaches
}

function useIds() {
	useStore($rerender)
	return ids
}

const useFile = (id: string) => useStore($fileMap, { keys: [id] })[id]

export {
	$fileState,
	add,
	clear,
	FilesState,
	getAttachments,
	remove,
	saveAttach,
	setError,
	updateLoaded,
	useFile,
	useIds
}
